"use client";

import Image from "next/image";
import Link from "next/link";
import React from "react";

interface Membership {
  membershipType?: string;
  associationFullName?: string;
  abbreviation?: string;
  year?: string;
  registrationNumber?: string;
}

interface SurgeonCredentialsProps {
  surgeonProfile?: {
    name?: string;
    shortEducation?: string;
    yearsOfExperience?: number;
    memberships?: Membership[];
    philosophy?: string;
    photoUrl?: string;
    fullProfileLink?: string;
  };
}

export default function SurgeonCredentials({
  surgeonProfile,
}: SurgeonCredentialsProps) {
  if (
    !surgeonProfile ||
    (!surgeonProfile.name &&
      !surgeonProfile.shortEducation &&
      !surgeonProfile.yearsOfExperience &&
      !surgeonProfile.memberships?.length)
  ) {
    return null;
  }

  const photoUrl = surgeonProfile.photoUrl || null;

  return (
    <section className="py-8">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 md:text-3xl">
          Hair Transplant Surgeon in Hyderabad
        </h2>
        <div className="relative mx-auto max-w-2xl rounded-2xl bg-violet-50 p-6 shadow-sm">
          <div className="flex gap-6">
            {/* Circular Photo on Left - Bigger */}
            {photoUrl && (
              <div className="relative flex-shrink-0">
                <div className="relative h-32 w-32 overflow-hidden rounded-full border-2 border-white shadow-md md:h-36 md:w-36">
                  <Image
                    src={photoUrl}
                    alt={surgeonProfile.name || "Surgeon"}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 128px, 144px"
                  />
                </div>
                {/* Optional verification checkmark */}
                <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
                  <svg
                    className="h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Credentials on Right */}
            <div className="flex-1 min-w-0 space-y-1.5">
              {/* Doctor Name - Bold */}
              {surgeonProfile.name && (
                <h3 className="text-lg font-bold text-gray-900 md:text-xl">
                  {surgeonProfile.name}
                </h3>
              )}

              {/* Qualifications */}
              {surgeonProfile.shortEducation && (
                <p className="text-sm text-gray-700 md:text-base">
                  {surgeonProfile.shortEducation}
                </p>
              )}

              {/* Memberships - Compact */}
              {surgeonProfile.memberships && surgeonProfile.memberships.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {surgeonProfile.memberships
                    .filter((membership) => membership.abbreviation)
                    .map((membership, index) => (
                      <span
                        key={index}
                        className="inline-block rounded bg-white px-2 py-0.5 text-xs font-medium text-gray-700 shadow-sm"
                      >
                        {membership.abbreviation}
                      </span>
                    ))}
                </div>
              )}

              {/* Philosophy Line */}
              {surgeonProfile.philosophy && (
                <p className="pt-2 text-xs italic text-gray-600 md:text-sm">
                  {surgeonProfile.philosophy}
                </p>
              )}
            </div>
          </div>
          {/* Full Profile Link - Bottom Right Corner */}
          {surgeonProfile.fullProfileLink && (
            <Link
              href={surgeonProfile.fullProfileLink}
              className="absolute bottom-4 right-4 flex items-center gap-1 text-sm font-medium text-violet-700 transition-colors hover:text-violet-900"
            >
              Full Profile
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

